import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../api/axios';
import WorkerBanner, { type WorkerStatus } from '../components/clients/WorkerBanner';
import InviteDialog, { type InviteTarget } from '../components/clients/InviteDialog';
import ReportTypeChip from '../components/ReportTypeChip';
import { ReportType } from '../types';

/* One screen for running clients: every client account with its members,
   invites, reports and messages. Billing lives in the Stripe Dashboard. */

interface ClientReport {
  id: number;
  uuid: string;
  period: string;
  facility_name: string;
  status: string;
  report_type?: ReportType;
  created_at: string | null;
  documents_count: number;
  warnings: string[];
  error_message: string;
}

interface ClientRequest {
  id: number;
  type: string; // contact_request, ...
  title: string;
  message: string;
  created_at: string;
  is_read: boolean;
}

interface ClientAccount {
  id: string;
  name: string;
  users: { id: number; email: string; name: string }[];
  invitations: {
    id: string;
    email: string;
    accepted: boolean;
    accept_url: string | null; // live invite link; null once accepted
  }[];
  reports: ClientReport[];
  requests: ClientRequest[];
}



const statusChip = (r: ClientReport) => {
  if (r.status === 'completed') return <Chip size="small" color="success" label="Completed" />;
  if (r.status === 'failed') return <Chip size="small" color="error" label="Failed" />;
  return <Chip size="small" label={r.status.charAt(0).toUpperCase() + r.status.slice(1)} />;
};


export default function ClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState<ClientAccount[]>([]);
  const [worker, setWorker] = useState<WorkerStatus | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');


  // invite dialog (Add member)
  const [invite, setInvite] = useState<InviteTarget | null>(null);

  // a bell notification links to /clients#acct-<id>: scroll that card into
  // view once, then drop the hash so later reloads (after an action) stay put
  useEffect(() => {
    if (loading || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  }, [loading]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get('/clients/overview/')
      .then((r) => {
        setClients(r.data.clients);
        setWorker(r.data.worker ?? null);
      })
      .catch((e) => setError(e.response?.data?.error || 'Failed to load clients.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // While anything is generating, refresh every 10s so status moves on its
  // own (pending -> running -> completed) without a manual reload.
  const generating = clients.some((c) => c.reports.some((r) => ['pending', 'running'].includes(r.status)));
  useEffect(() => {
    if (!generating) return;
    const id = window.setInterval(() => {
      api.get('/clients/overview/').then((r) => setClients(r.data.clients)).catch(() => {});
    }, 10000);
    return () => window.clearInterval(id);
  }, [generating]);

  const copyText = async (text: string, note: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage(note);
    } catch {
      setError('Could not copy; your browser blocked clipboard access.');
    }
  };

  // Archive: the row and its files stay, the report leaves every list. Two clicks: the first arms the button.
  const [armedDelete, setArmedDelete] = useState<string | null>(null);
  const deleteReport = async (r: ClientReport) => {
    if (armedDelete !== r.uuid) {
      setArmedDelete(r.uuid);
      setTimeout(() => setArmedDelete((cur) => (cur === r.uuid ? null : cur)), 4000);
      return;
    }
    setArmedDelete(null);
    setError('');
    try {
      await api.delete(`/reports/${r.uuid}/delete/`);
      setMessage(`${r.facility_name} deleted.`);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to delete.');
    }
  };

  const requeue = async (uuid: string) => {
    setError('');
    try {
      await api.post(`/reports/${uuid}/requeue/`);
      setMessage('Requeued.');
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to requeue.');
    }
  };

  const generateReport = async (r: ClientReport) => {
    setError('');
    try {
      await api.post(`/reports/${r.uuid}/approve/`);
      setMessage(`Generating ${r.facility_name} — refresh in ~a minute.`);
      load();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to start generation.');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ color: '#102B3D' }}>
          Clients
        </Typography>
      </Box>

      <WorkerBanner worker={worker} onRequeue={requeue} />
      {message && (
        <Alert severity="success" onClose={() => setMessage('')} sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : clients.length === 0 ? (
        <Paper variant="outlined" square sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No client accounts yet. Create one on the Accounts page.
          </Typography>
        </Paper>
      ) : (
        clients.map((c) => {
          const showAll = !!expanded[c.id];
          const visibleReports = showAll ? c.reports : c.reports.slice(0, 3);
          const pending = c.invitations.filter((i) => !i.accepted);
          return (
          <Paper key={c.id} id={`acct-${c.id}`} variant="outlined" square sx={{ mb: 2.5, overflow: 'hidden', borderColor: '#A9B8C4' }}>
            {/* who, actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 1.25, bgcolor: '#EAF1F5', borderBottom: '1px solid #A9B8C4' }}>
              <Typography fontWeight={700} sx={{ color: '#102B3D' }}>
                {c.name}
              </Typography>
              {c.requests.filter((q) => !q.is_read).length > 0 && (
                <Link component="button" variant="body2" onClick={() => navigate(`/alerts?client=${c.id}`)} sx={{ fontWeight: 600 }}>
                  {c.requests.filter((q) => !q.is_read).length} request{c.requests.filter((q) => !q.is_read).length === 1 ? '' : 's'}
                </Link>
              )}
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAddAltOutlinedIcon />}
                  onClick={() => setInvite({ account: c })}
                >
                  Add member
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<NoteAddOutlinedIcon />}
                  onClick={() => navigate(`/?account=${c.id}`)}
                >
                  New report
                </Button>
              </Box>
            </Box>


            {/* members */}
            {/* one line per person: email, invite state */}
            <Box sx={{ px: 2.5, py: 1, borderBottom: '1px solid #EEF2F5' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Members</Typography>
              {c.users.length === 0 && pending.length === 0 && (
                <Typography variant="body2" color="text.secondary">none yet</Typography>
              )}
              {c.users.map((u) => (
                <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.25 }}>
                  <Typography variant="body2" sx={{ minWidth: 280 }}>{u.email}</Typography>
                </Box>
              ))}
              {pending.map((i) => (
                <Box key={i.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 0.25 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 280 }}>{i.email}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    invited
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={() => i.accept_url && copyText(i.accept_url, `Join link for ${i.email} copied.`)}
                    sx={{ textTransform: 'none', py: 0 }}
                  >
                    Copy join link
                  </Button>
                </Box>
              ))}
            </Box>

            {/* reports */}
            <Box sx={{ px: 2.5, pt: 1.25, pb: c.reports.length ? 0.5 : 1.25 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                Reports ({c.reports.length})
              </Typography>
            </Box>
            {c.reports.length > 0 && (
              <Table size="small" sx={{ tableLayout: 'fixed' /* columns line up from card to card */ }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Facility</TableCell>
                    <TableCell sx={{ width: 120 }}>Created</TableCell>
                    <TableCell sx={{ width: 220 }}>Status</TableCell>
                    <TableCell align="right" sx={{ width: 300 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleReports.map((r) => (
                    <TableRow key={r.uuid} hover>
                      <TableCell>
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ mr: 1 }}>#{r.id}</Typography>
                        <Link
                          component="button"
                          onClick={() => navigate(`/reports/${r.uuid}?tab=documents`)}
                          sx={{ fontWeight: 600 }}
                        >
                          {r.facility_name}
                        </Link>
                        {r.period && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{r.period}</Typography>
                        )}
                        <Box component="span" sx={{ ml: 1 }}><ReportTypeChip type={r.report_type} /></Box>
                      </TableCell>
                      <TableCell>{r.created_at ?? ''}</TableCell>
                      <TableCell>
                        {statusChip(r)}
                        {r.status === 'completed' && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${r.documents_count} docs`}
                            onClick={() => navigate(`/reports/${r.uuid}?tab=documents`)}
                            sx={{ ml: 0.5 }}
                          />
                        )}
                        {r.warnings.length > 0 && (
                          <Tooltip title={r.warnings.join(' — ')}>
                            <Chip size="small" color="warning" label="⚠ review" sx={{ ml: 0.5 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {r.status === 'requested' && r.report_type === 'mediation' && (
                          <Button size="small" onClick={() => navigate(`/reports/${r.uuid}`)}>
                            Upload
                          </Button>
                        )}
                        {(r.status === 'requested' || r.status === 'invoiced') && r.report_type !== 'mediation' && (
                          <Button size="small" onClick={() => generateReport(r)}>
                            Generate
                          </Button>
                        )}
                        {r.status === 'failed' && (
                          <Tooltip title={r.error_message || 'Generation failed'}>
                            <Button size="small" color="error" onClick={() => generateReport(r)}>
                              Retry
                            </Button>
                          </Tooltip>
                        )}
                        <Button
                          size="small"
                          color="error"
                          variant={armedDelete === r.uuid ? 'contained' : 'text'}
                          onClick={() => deleteReport(r)}
                        >
                          {armedDelete === r.uuid ? 'Confirm delete' : 'Delete'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {c.reports.length > 3 && (
              <Box sx={{ px: 2.5, py: 0.75, borderTop: '1px solid #EEF2F5' }}>
                <Button size="small" onClick={() => setExpanded({ ...expanded, [c.id]: !showAll })}>
                  {showAll ? 'Show fewer' : `Show all ${c.reports.length}`}
                </Button>
              </Box>
            )}
          </Paper>
          );
        })
      )}

      {/* Invite dialog: creates the login, shows the link, emails nobody */}
      <InviteDialog
        target={invite}
        onClose={() => setInvite(null)}
        onCreated={load}
        onMessage={setMessage}
        onError={setError}
      />


    </Box>
  );
}
